// Seeded FR/EN templates (spec 08 §3).
// DRAFT COPY: the owner approves it (shown in French) before Task 22 seeds staging/production.
// Owner-requested changes are edited in this file only.
// Brochure link points to https://gavion.ai until the owner provides a brochure URL.
import { extractVariables } from "./render.ts";
import type { TemplateRow } from "./types.ts";

/** True while the copy below is awaiting owner approval; seeding must not ship it to production as final. */
export const DEFAULT_TEMPLATES_DRAFT = true as const;

/**
 * GAVION missed-call (owner request 2026-10-15): « Appel manqué — Gala », the owner's own text. Sent once per lead by
 * rule R25 (automation/missedCallRules.ts) after « Pas de réponse » / « Message laissé ». Unlike the copy above it is
 * seeded APPROVED by 55_gavion_missed_call_email.sql (its `BEGIN missed-call-templates` literal equals the two rows
 * below; scripts/gavion/opsParity.test.ts). Automated sends carry no attachment: the brochure is a link
 * ({{brochure_url}}, Paramètres → IA) and the sentence disappears when the link is empty.
 */
export const MISSED_CALL_TEMPLATE_SLUG = "missed_call";

export type DefaultTemplate = Omit<TemplateRow, "id" | "body_text"> & {
  slug: string;
  body_text: null;
  variables: string[];
};

const HI_FR =
  "<p>Bonjour{{#if contact.first_name}} {{contact.first_name}}{{/if}},</p>";
const HI_EN =
  "<p>Hello{{#if contact.first_name}} {{contact.first_name}}{{/if}},</p>";
const SIGN = (closing: string) =>
  `<p>${closing},<br>{{rep.full_name}}<br>Gavion</p>`;

type Draft = Omit<DefaultTemplate, "variables" | "body_text">;
const DRAFTS: readonly Draft[] = [
  {
    slug: "voicemail_followup",
    name: "Suivi message laissé",
    kind: "one_to_one",
    language: "fr",
    subject: "Suite à mon appel — {{company.name}}",
    preheader: "Je vous ai laissé un message récemment.",
    body_html:
      HI_FR +
      "<p>Je vous ai laissé un message récemment, après avoir tenté de vous joindre. Je m'appelle {{rep.first_name}} et je travaille chez Gavion : nous aidons les entreprises comme {{company.name}} à répondre à chaque appel et à chaque demande de client, même quand l'équipe est occupée.</p>" +
      "<p>Auriez-vous 15 minutes cette semaine pour en discuter ? Il suffit de répondre à ce courriel{{#if rep.phone}} ou de m'appeler au {{rep.phone}}{{/if}}.</p>" +
      SIGN("Bonne journée"),
  },
  {
    slug: "voicemail_followup",
    name: "Voicemail follow-up",
    kind: "one_to_one",
    language: "en",
    subject: "Following up on my call — {{company.name}}",
    preheader: "I recently left you a message.",
    body_html:
      HI_EN +
      "<p>I recently tried to reach you and left you a message. My name is {{rep.first_name}} and I work at Gavion: we help businesses like {{company.name}} answer every call and every customer request, even when the team is busy.</p>" +
      "<p>Would you have 15 minutes this week to talk about it? Just reply to this email{{#if rep.phone}} or call me at {{rep.phone}}{{/if}}.</p>" +
      SIGN("Have a great day"),
  },
  {
    slug: "demo_confirmation",
    name: "Confirmation de démo",
    kind: "transactional",
    language: "fr",
    subject:
      "Votre démo Gavion est confirmée{{#if deal.demo_at_fr}} — {{deal.demo_at_fr}}{{/if}}",
    preheader: "Merci pour votre temps au téléphone.",
    body_html:
      HI_FR +
      "<p>Merci pour votre temps au téléphone. Votre démonstration de Gavion pour {{company.name}} est confirmée{{#if deal.demo_at_fr}} le {{deal.demo_at_fr}} (heure de l'Est){{/if}}.</p>" +
      '{{#if deal.teams_join_url}}<p><a href="{{deal.teams_join_url}}">Rejoindre la réunion Teams</a></p>{{/if}}' +
      "<p>En 30 minutes, nous verrons comment Gavion peut prendre vos appels, qualifier vos demandes et remplir votre agenda. Si l'heure ne vous convient plus, répondez simplement à ce courriel.</p>" +
      SIGN("À bientôt"),
  },
  {
    slug: "demo_confirmation",
    name: "Demo confirmation",
    kind: "transactional",
    language: "en",
    subject:
      "Your Gavion demo is confirmed{{#if deal.demo_at_en}} — {{deal.demo_at_en}}{{/if}}",
    preheader: "Thank you for your time on the phone.",
    body_html:
      HI_EN +
      "<p>Thank you for your time on the phone. Your Gavion demo for {{company.name}} is confirmed{{#if deal.demo_at_en}} for {{deal.demo_at_en}} (Eastern time){{/if}}.</p>" +
      '{{#if deal.teams_join_url}}<p><a href="{{deal.teams_join_url}}">Join the Teams meeting</a></p>{{/if}}' +
      "<p>In 30 minutes, we will show how Gavion can answer your calls, qualify your requests and fill your calendar. If the time no longer works for you, just reply to this email.</p>" +
      SIGN("See you soon"),
  },
  {
    slug: "brochure",
    name: "Brochure",
    kind: "one_to_one",
    language: "fr",
    subject: "Notre brochure, comme promis",
    preheader: "L'essentiel de Gavion en quelques minutes.",
    body_html:
      HI_FR +
      '<p>Comme convenu au téléphone, voici notre brochure : <a href="https://gavion.ai">gavion.ai</a>. Vous y verrez comment Gavion aide des entreprises comme {{company.name}} à ne plus manquer d\'appels ni de rendez-vous.</p>' +
      "<p>Je vous rappelle dans quelques jours pour répondre à vos questions. D'ici là, n'hésitez pas à me répondre directement.</p>" +
      SIGN("Bonne journée"),
  },
  {
    slug: "brochure",
    name: "Brochure",
    kind: "one_to_one",
    language: "en",
    subject: "Our brochure, as promised",
    preheader: "Gavion in a few minutes.",
    body_html:
      HI_EN +
      '<p>As discussed on the phone, here is our brochure: <a href="https://gavion.ai">gavion.ai</a>. It shows how Gavion helps businesses like {{company.name}} stop missing calls and appointments.</p>' +
      "<p>I will call you back in a few days to answer your questions. In the meantime, you can reply to this email directly.</p>" +
      SIGN("Have a great day"),
  },
  {
    slug: "proposal_followup",
    name: "Relance proposition",
    kind: "one_to_one",
    language: "fr",
    subject: "Votre proposition Gavion — {{company.name}}",
    preheader: "Avez-vous eu le temps de la regarder ?",
    body_html:
      HI_FR +
      "<p>Je fais un suivi au sujet de la proposition que nous vous avons envoyée pour {{company.name}}. Avez-vous eu le temps de la regarder ?</p>" +
      "<p>Ce serait un plaisir de répondre à vos questions ou d'ajuster l'offre à vos besoins. Quel moment vous conviendrait pour un court appel ?</p>" +
      SIGN("Merci"),
  },
  {
    slug: "proposal_followup",
    name: "Proposal follow-up",
    kind: "one_to_one",
    language: "en",
    subject: "Your Gavion proposal — {{company.name}}",
    preheader: "Have you had a chance to look at it?",
    body_html:
      HI_EN +
      "<p>I am following up on the proposal we sent for {{company.name}}. Have you had a chance to look at it?</p>" +
      "<p>I would be happy to answer your questions or adjust the offer to your needs. When would be a good time for a short call?</p>" +
      SIGN("Thank you"),
  },
  // GAVION missed-call: the owner's text (FR) and its translation (EN); see MISSED_CALL_TEMPLATE_SLUG
  {
    slug: MISSED_CALL_TEMPLATE_SLUG,
    name: "Appel manqué — Gala",
    kind: "one_to_one",
    language: "fr",
    subject: "Votre appel manqué — {{company.name}}",
    preheader: "Cet appel manqué aurait pu vous coûter cher.",
    body_html:
      HI_FR +
      "<p>Je viens d'appeler votre réception et je suis tombé sur la boîte vocale. Heureusement, nous avons une solution à vous proposer, mais cet appel manqué aurait pu vous coûter cher.</p>" +
      "<p>Chaque appel qui tombe sur la boîte vocale, c'est un client qui appelle votre concurrent. Deux ou trois par semaine, multipliés par la valeur d'un contrat : faites le calcul.</p>" +
      "<p>Gala, notre assistante IA, répond à votre place, instantanément :</p>" +
      "<ul><li>24/7, en français et en anglais, urgences acheminées à votre équipe</li>" +
      "<li>Rendez-vous réservés dans votre calendrier pendant l'appel</li>" +
      "<li>Soumissions préliminaires à partir d'une photo ou d'une adresse</li>" +
      "<li>Vous gardez votre numéro actuel</li></ul>" +
      "<p>Personnalisée à votre entreprise, avec des forfaits d'accompagnement pour la configurer. On ne remplace pas votre équipe, on lui redonne ses heures.</p>" +
      '<p>Essayez-la au 514 534-1298, comme le ferait un de vos clients.{{#if brochure_url}} Voici <a href="{{brochure_url}}">notre brochure</a>.{{/if}}</p>' +
      "<p>Une démo de 30 minutes semaine prochaine?</p>" +
      SIGN("Bonne journée"),
  },
  {
    slug: MISSED_CALL_TEMPLATE_SLUG,
    name: "Missed call — Gala",
    kind: "one_to_one",
    language: "en",
    subject: "Your missed call — {{company.name}}",
    preheader: "That missed call could have cost you dearly.",
    body_html:
      HI_EN +
      "<p>I just called your front desk and reached voicemail. Fortunately, we have a solution to offer you, but this missed call could have cost you dearly.</p>" +
      "<p>Every call that goes to voicemail is a customer calling your competitor. Two or three a week, multiplied by the value of a contract: do the math.</p>" +
      "<p>Gala, our AI assistant, answers for you, instantly:</p>" +
      "<ul><li>24/7, in French and English, emergencies routed to your team</li>" +
      "<li>Appointments booked in your calendar during the call</li>" +
      "<li>Preliminary quotes from a photo or an address</li>" +
      "<li>You keep your current number</li></ul>" +
      "<p>Customized to your business, with support packages to set it up. We don't replace your team, we give it its hours back.</p>" +
      '<p>Try it at 514 534-1298, just like one of your customers would.{{#if brochure_url}} Here is <a href="{{brochure_url}}">our brochure</a>.{{/if}}</p>' +
      "<p>A 30-minute demo next week?</p>" +
      SIGN("Have a great day"),
  },
  // GAVION X4: starter sequences « Après envoi de la brochure » and « Après démo » (second nudge)
  {
    slug: "brochure_followup",
    name: "Suivi brochure",
    kind: "one_to_one",
    language: "fr",
    subject: "Avez-vous pu parcourir la brochure ?",
    preheader: "Quelques minutes pour en parler ?",
    body_html:
      HI_FR +
      "<p>Je vous ai envoyé notre brochure il y a quelques jours. Avez-vous pu la parcourir ? Ce serait un plaisir d'échanger sur ce que Gavion pourrait apporter à {{company.name}}.</p>" +
      "<p>Auriez-vous 15 minutes cette semaine pour un court appel ? Il suffit de répondre à ce courriel{{#if rep.phone}} ou de m'appeler au {{rep.phone}}{{/if}}.</p>" +
      SIGN("Bonne journée"),
  },
  {
    slug: "brochure_followup",
    name: "Brochure follow-up",
    kind: "one_to_one",
    language: "en",
    subject: "Did you get a chance to look at the brochure?",
    preheader: "A few minutes to talk about it?",
    body_html:
      HI_EN +
      "<p>I sent you our brochure a few days ago. Did you get a chance to look at it? I would be glad to talk about what Gavion could bring to {{company.name}}.</p>" +
      "<p>Would you have 15 minutes this week for a short call? Just reply to this email{{#if rep.phone}} or call me at {{rep.phone}}{{/if}}.</p>" +
      SIGN("Have a great day"),
  },
  {
    slug: "demo_followup_reminder",
    name: "Relance après démo",
    kind: "one_to_one",
    language: "fr",
    subject: "Suite à notre démo — {{company.name}}",
    preheader: "Des questions depuis notre rencontre ?",
    body_html:
      HI_FR +
      "<p>Je reviens vers vous suite à notre démo de Gavion. Avez-vous des questions, ou souhaitez-vous que nous ajustions la proposition aux besoins de {{company.name}} ?</p>" +
      "<p>Si le moment n'est pas idéal, dites-le-moi simplement : je vous recontacterai plus tard.</p>" +
      SIGN("Merci"),
  },
  {
    slug: "demo_followup_reminder",
    name: "Post-demo reminder",
    kind: "one_to_one",
    language: "en",
    subject: "Following our demo — {{company.name}}",
    preheader: "Any questions since we met?",
    body_html:
      HI_EN +
      "<p>I am following up after our Gavion demo. Do you have any questions, or would you like us to adjust the proposal to the needs of {{company.name}}?</p>" +
      "<p>If the timing is not right, just let me know and I will reach out later.</p>" +
      SIGN("Thank you"),
  },
  {
    slug: "newsletter_base",
    name: "Infolettre — modèle de base",
    kind: "marketing",
    language: "fr",
    subject: "Les nouvelles de Gavion",
    preheader: "Ce qui change ce mois-ci chez Gavion.",
    body_html:
      "<h2>Les nouvelles de Gavion</h2>" +
      HI_FR +
      "<p>Voici ce qui change ce mois-ci chez Gavion, et ce que cela peut apporter à {{company.name}}.</p>" +
      "<h3>Nouveauté</h3><p>[[À compléter : la nouveauté du mois en deux ou trois phrases.]]</p>" +
      "<h3>Conseil pratique</h3><p>[[À compléter : un conseil utile pour les entreprises de services.]]</p>" +
      '<p><a href="https://gavion.ai">Découvrir Gavion</a></p>' +
      "<p>Merci de votre confiance,<br>L'équipe Gavion</p>",
  },
  {
    slug: "newsletter_base",
    name: "Newsletter — base template",
    kind: "marketing",
    language: "en",
    subject: "News from Gavion",
    preheader: "What is new at Gavion this month.",
    body_html:
      "<h2>News from Gavion</h2>" +
      HI_EN +
      "<p>Here is what is new at Gavion this month, and what it can bring to {{company.name}}.</p>" +
      "<h3>What's new</h3><p>[[To complete: this month's update in two or three sentences.]]</p>" +
      "<h3>Practical tip</h3><p>[[To complete: a useful tip for service businesses.]]</p>" +
      '<p><a href="https://gavion.ai">Discover Gavion</a></p>' +
      "<p>Thank you for your trust,<br>The Gavion team</p>",
  },
];

export const DEFAULT_TEMPLATES: readonly DefaultTemplate[] = Object.freeze(
  DRAFTS.map((d) =>
    Object.freeze({
      ...d,
      body_text: null,
      variables: [
        ...new Set(
          [d.subject, d.preheader ?? "", d.body_html].flatMap(extractVariables),
        ),
      ],
    }),
  ),
);
